import Axios from "axios"
import { useMemo, useRef, useState } from "react"
import { Button } from "react-bootstrap"
import { CSVLink } from "react-csv"
import DoublePanel, {
	sortCaret,
} from "src/components/common/DoublePanel/DoublePanel"
import Loader from "src/components/common/Loader"
import IconBuilder from "src/utilities/iconBuilder/IconBuilder"
import AxiosConfig from "src/utilities/AxiosConfig"
import snackBar from "src/utilities/snackBar/SnackBar"
import type { I_LeaseImportResult, LeaseImportReasonCode } from "./types"
import "./LeaseImport.css"

/**
 * Uploads a lease CSV to the bulk import endpoint.
 *
 * @param file - The CSV chosen by the user.
 * @returns The per-row import result, or null when the request failed outright.
 */
const postLeaseImport = async (
	file: File,
): Promise<I_LeaseImportResult | null> => {
	const form = new FormData()
	// Any part carrying a filename is accepted; the endpoint takes the first one.
	form.append("file", file, file.name)

	try {
		const response = await Axios.post<I_LeaseImportResult>(
			`${process.env.REACT_APP_API_URL}/api/fleet/leases/import`,
			form,
			AxiosConfig.configValue("min"),
		)
		return response.data
	} catch (error: unknown) {
		console.error("postLeaseImport failed:", error)
		const response = (
			error as { response?: { status?: number; data?: unknown } } | undefined
		)?.response
		const body = response?.data

		let serverMessage: string | null = null
		if (typeof body === "string" && body.trim()) {
			serverMessage = body
		} else if (body && typeof body === "object") {
			const aspNet = body as { Message?: string; ExceptionMessage?: string }
			serverMessage = aspNet.ExceptionMessage || aspNet.Message || null
		} else if (!response) {
			serverMessage = "Could not reach the API. Check the connection and CORS."
		}

		const status = response?.status ? ` (HTTP ${response.status})` : ""

		snackBar({
			type: "error",
			text: `${serverMessage || "Failed to import the lease file"}${status}`,
			timeout: 5000,
		})
		return null
	}
}

/**
 * The columns the import expects, in template order. Kept here so the template
 * download stays in step with LeaseImportCsvParser.RequiredHeaders on the API.
 */
const TEMPLATE_HEADERS = [
	"VIN",
	"Unit #",
	"Date Received",
	"Capitalized Amount",
	"Interest Rate (%)",
	"Lease Depreciation (%)",
	"Mgmt Fee (%)",
	"Maint Pgm Mo Exp",
	"Lease Length (Months)",
	"Period Zero Lease Exp",
	"Pd Zero Depr",
	"End of Lease Svc Chg",
	"Sls Tx Per Mo",
	"Month of 1st Pmt",
	"Sls Tx on Incentive",
	"EFM Negotiated Rate",
	"Down Payment",
	"Gain on Prior Vehicle",
	"Monthly Exp Override",
]

const statusClass = (status: string) =>
	`lease-import-status lease-import-status--${String(status).toLowerCase()}`

/**
 * Readable labels for LeaseImportReasonCode. Written out rather than derived by
 * splitting the PascalCase, because the acronyms matter: VIN and EFM must stay
 * upper-case, which a generic word-splitter renders as "Vin" and "Efm".
 *
 * "None" maps to an empty string - imported rows have no reason to report.
 */
const REASON_LABELS: Record<LeaseImportReasonCode, string> = {
	None: "",
	MissingRequiredValue: "Missing Required Value",
	InvalidValueFormat: "Invalid Value Format",
	LeaseLengthOutOfRange: "Lease Length Out Of Range",
	UnitNumberTooLong: "Unit Number Too Long",
	VinNotFound: "VIN Not Found",
	VinAmbiguous: "VIN Ambiguous",
	DuplicateVinInFile: "Duplicate VIN In File",
	CapitalLeaseTestFailed: "Capital Lease Test Failed",
	PaymentMonthConflict: "Payment Month Conflict",
	VehicleAlreadyLeased: "Vehicle Already Leased",
	DatabaseRejected: "Database Rejected",
}

/**
 * Falls back to splitting the raw code if the API ever returns one this build
 * has no label for - a readable guess beats a blank cell.
 */
const reasonLabel = (code: LeaseImportReasonCode | string): string => {
	const known = REASON_LABELS[code as LeaseImportReasonCode]
	if (known !== undefined) return known
	return String(code).replace(/([a-z])([A-Z])/g, "$1 $2")
}

const LeaseImport = () => {
	const [file, setFile] = useState<File | null>(null)
	const [uploading, setUploading] = useState(false)
	const [result, setResult] = useState<I_LeaseImportResult | null>(null)
	const fileInput = useRef<HTMLInputElement>(null)

	const rows = result?.Rows ?? []

	const failedRows = useMemo(
		() => result?.Rows.filter((r) => r.Status === "Failed") ?? [],
		[result],
	)

	const sortableHeader = (column: any, _colIndex: number, components: any) => (
		<div className="d-flex align-items-center Lease-Import-Header-Formatter">
			{column.text}
			{components.sortElement}
		</div>
	)

	const columns = [
		{
			sort: true,
			sortCaret: sortCaret,
			text: "CSV Row",
			dataField: "RowNumber",
			headerFormatter: sortableHeader,
		},
		{
			sort: true,
			sortCaret: sortCaret,
			text: "VIN",
			dataField: "Vin",
			formatter: (cell: any) => cell || "",
			headerFormatter: sortableHeader,
		},
		{
			sort: true,
			sortCaret: sortCaret,
			text: "Status",
			dataField: "Status",
			formatter: (cell: any) => (
				<span className={statusClass(cell)}>{cell}</span>
			),
			headerFormatter: sortableHeader,
		},
		{
			sort: true,
			sortCaret: sortCaret,
			text: "Reason",
			dataField: "ReasonCode",
			formatter: (cell: any) => reasonLabel(cell),
			headerFormatter: sortableHeader,
		},
		{
			sort: true,
			sortCaret: sortCaret,
			text: "Message",
			dataField: "Message",
			formatter: (cell: any) => cell || "",
			headerFormatter: sortableHeader,
		},
	]

	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setFile(event.target.files?.[0] ?? null)
		// A previous result belongs to a previous file - clear it so the two are
		// never shown together.
		setResult(null)
	}

	const handleUpload = async () => {
		if (!file) return
		setUploading(true)
		const response = await postLeaseImport(file)
		setUploading(false)
		if (response) setResult(response)
	}

	const handleReset = () => {
		setFile(null)
		setResult(null)
		if (fileInput.current) fileInput.current.value = ""
	}

	return (
		<div className="LeaseImport">
			<h4>Import Leases</h4>
			<p className="lease-import-description">
				Create many vehicle leases at once from a single CSV file, instead of
				entering them one at a time. Once a file is uploaded, a results table is
				generated showing what happened to every row in it. A file can be
				corrected and uploaded again as many times as needed - any vehicle that
				already has an active lease is skipped rather than duplicated, so
				re-uploading will not create a second lease for it. Follow the steps
				below.
			</p>
			<div className="lease-import-steps">
				<div className="lease-import-step">
					<h5>Step 1</h5>
					<p>
						<CSVLink
							data={[]}
							headers={TEMPLATE_HEADERS.map((h) => ({ label: h, key: h }))}
							filename="VehicleLease_Import_Template.csv"
							title="Download the expected column template"
						>
							Download the lease template
						</CSVLink>
						.
					</p>
				</div>

				<div className="lease-import-step">
					<h5>Step 2</h5>
					<p>Fill out the template, then save the file.</p>
					<p className="lease-import-note">
						<strong>Note:</strong> every column requires a value, except Monthly
						Exp Override. Fill that one in only when the monthly payment is
						being overridden, otherwise leave it blank. A blank is not the same
						as a zero, which would be treated as an override of $0.
					</p>
					<p className="lease-import-note">
						<strong>Note:</strong> percentage columns of Interest Rate (%),
						Lease Depreciation (%) and Mgmt Fee (%) must be written as the
						percentage itself, not already divided by 100. For example, An
						interest rate of 7.32% is entered as 7.32, not 0.0732.
					</p>
				</div>

				<div className="lease-import-step">
					<h5>Step 3</h5>
					<p>
						Choose the saved file below and click <strong>Upload</strong>.
					</p>
				</div>

				<div className="lease-import-step">
					<h5>Step 4</h5>
					<p>
						Review the results. Every row is reported individually, so rows that
						fail can be corrected and re-uploaded without creating duplicates.
					</p>
				</div>
			</div>

			<div className="lease-import-controls">
				<input
					ref={fileInput}
					type="file"
					accept=".csv,text/csv"
					onChange={handleFileChange}
					disabled={uploading}
					aria-label="Lease CSV file"
				/>
				<Button
					variant="primary"
					onClick={handleUpload}
					disabled={!file || uploading}
				>
					Upload
				</Button>
				{(file || result) && (
					<Button
						variant="secondary"
						onClick={handleReset}
						disabled={uploading}
					>
						Clear
					</Button>
				)}
			</div>

			{uploading && <Loader />}

			{result?.HeaderValidationFailed && (
				<div className="lease-import-header-failure">
					<h5>This file was not imported</h5>
					<p>
						The following required{" "}
						{result.MissingColumns.length === 1 ? "column is" : "columns are"}{" "}
						missing, so no rows were read:
					</p>
					<ul>
						{result.MissingColumns.map((c) => (
							<li key={c}>{c}</li>
						))}
					</ul>
					<p>Add them to the file and upload it again.</p>
				</div>
			)}

			{result && !result.HeaderValidationFailed && (
				<>
					<h5 className="lease-import-results-title">Import Results</h5>

					<div className="lease-import-summary">
						<span className="lease-import-count">
							<strong>{result.TotalRows}</strong> rows read
						</span>
						<span className="lease-import-count lease-import-count--imported">
							<strong>{result.ImportedCount}</strong> imported
						</span>
						<span className="lease-import-count lease-import-count--skipped">
							<strong>{result.SkippedCount}</strong> skipped
						</span>
						<span className="lease-import-count lease-import-count--failed">
							<strong>{result.FailedCount}</strong> failed
						</span>
					</div>

					{result.SkippedCount > 0 && (
						<p className="lease-import-skipped-note">
							Skipped rows already had an active lease. Nothing was changed for
							them and no duplicates were created - this is not an error.
						</p>
					)}

					<div className="lease-import-actions">
						{failedRows.length > 0 && (
							<CSVLink
								data={failedRows.map((r) => ({
									RowNumber: r.RowNumber,
									Vin: r.Vin ?? "",
									ReasonCode: reasonLabel(r.ReasonCode),
									Message: r.Message ?? "",
								}))}
								headers={[
									{ label: "Row", key: "RowNumber" },
									{ label: "VIN", key: "Vin" },
									{ label: "Reason", key: "ReasonCode" },
									{ label: "Message", key: "Message" },
								]}
								filename="LeaseImport_FailedRows.csv"
								className="lease-import-failed-link"
								title="Download the failed rows as CSV"
							>
								<IconBuilder extension="csv" size={32} />
								<span>Download failed rows</span>
							</CSVLink>
						)}
					</div>

					<DoublePanel
						manual
						url=""
						data={rows}
						keyField="RowNumber"
						wrapperClassName="LeaseImportTable"
						turnIntoModal={false}
						// The row detail is a handful of fields - it does not need the 70%
						// default, which would squeeze the results table to a third.
						secondaryInitialSize={35}
						secondaryMinSize={25}
						columns={columns}
						searchList={["RowNumber", "Vin", "Status", "ReasonCode", "Message"]}
						DetailPanel={({ details, handleClose }: any) => (
							<div className="lease-import-detail">
								<div className="lease-import-detail-head">
									<h5>Row {details?.RowNumber}</h5>
									<button
										type="button"
										className="lease-import-detail-close"
										onClick={handleClose}
										aria-label="Close row details"
									>
										<span className="material-icons">close</span>
									</button>
								</div>
								<dl>
									<dt>VIN</dt>
									<dd>{details?.Vin || "-"}</dd>
									<dt>Status</dt>
									<dd className={statusClass(details?.Status)}>
										{details?.Status}
									</dd>
									<dt>Reason</dt>
									<dd>{reasonLabel(details?.ReasonCode) || "-"}</dd>
									<dt>Message</dt>
									<dd>{details?.Message || "-"}</dd>
									{details?.VehicleLeaseId && (
										<>
											<dt>Lease ID</dt>
											<dd>{details.VehicleLeaseId}</dd>
										</>
									)}
								</dl>
							</div>
						)}
						Header={({ searchValue, onSearchChange }: any) => (
							<div className="lease-import-search">
								<input
									type="search"
									value={searchValue}
									onChange={onSearchChange}
									placeholder="Filter by status, VIN, row or message"
									aria-label="Filter import results"
								/>
							</div>
						)}
					/>

					{/* Same action as the Clear button above - repeated here so a long
					    results table does not have to be scrolled back up. */}
					<div className="lease-import-footer-actions">
						<Button
							variant="secondary"
							onClick={handleReset}
							disabled={uploading}
						>
							Clear
						</Button>
					</div>
				</>
			)}
		</div>
	)
}

export default LeaseImport
